import { EllipsisHorizontalIcon } from "@heroicons/react/24/outline";
import { useMemo, useState, useRef, memo } from "react";
import { ExpandableTextarea } from "@/core/components/expandable-textarea";
import { Button } from "@/core/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/core/components/ui/dropdown-menu";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Separator } from "@/core/components/ui/separator";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import { dayjs } from "@/core/utils/date";
import { formatDate } from "@/core/utils/date";
import { getAttributedUserName } from "@/core/utils/name";
import { INote } from "@/customer/customer";
import { useCreateCustomerNote, useDeleteCustomerNote } from "@/customer/query";
import "@/core/components/expandable-textarea/stylesheet.css";

const NoteDeleteDropdown = ({ onDelete }: { onDelete?: () => void }) => {
  const handleDelete = () => !!onDelete && onDelete();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="icon"
          size="iconSm"
          type="button"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <EllipsisHorizontalIcon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {onDelete && (
          <DropdownMenuItem onClick={handleDelete}>
            Delete Note
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

interface NoteItemProps {
  note: INote;
  isLast?: boolean;
  customerId?: string;
  onDeleteNote?: (noteId: string) => void;
}

const NoteItem = memo(
  ({ note, isLast = false, customerId, onDeleteNote }: NoteItemProps) => (
    <Stack gap="none" fullWidth>
      <Stack
        direction="row"
        fullWidth
        justify="between"
        className="items-center"
      >
        <Stack
          direction="row"
          gap="sm"
          className="flex-shrink-0 items-baseline"
        >
          <Typography variant="bodyW1">
            {getAttributedUserName(note.user) || "Unknown user"}
          </Typography>
          <Typography variant="bodyW3" textColor="tertiary" className="font-sm">
            {formatDate(note.created_at)}
          </Typography>
        </Stack>
        {customerId && onDeleteNote && (
          <NoteDeleteDropdown onDelete={() => onDeleteNote(note.id)} />
        )}
      </Stack>
      <Stack gap="sm" fullWidth>
        <Typography variant="bodyW1" className="font-mdsm whitespace-pre-wrap">
          {note.content}
        </Typography>
      </Stack>
      {!isLast && <Separator className="my-3" />}
    </Stack>
  ),
);

interface NoteListProps {
  notes: INote[];
  customerId?: string;
}

export const NoteList = ({ notes, customerId }: NoteListProps) => {
  const [noteText, setNoteText] = useState("");
  const textarearef = useRef<HTMLTextAreaElement>(null);
  const { mutate: addCustomerNote, isPending } = useCreateCustomerNote();
  const { mutate: deleteCustomerNote } = useDeleteCustomerNote();

  const sortedNotes = useMemo(
    () =>
      [...notes].sort((a, b) => dayjs(b.created_at).diff(dayjs(a.created_at))),
    [notes],
  );

  const handleAddNote = () => {
    if (!customerId || !noteText.trim()) return;

    addCustomerNote(
      { customerId, content: noteText.trim() },
      {
        onSuccess: () => {
          toast.success("Note added successfully");
          setNoteText("");
          textarearef.current?.blur();
        },
        onError: (error: Error) => {
          toast.error("Failed to add note", {
            description:
              error instanceof Error ? error.message : "Please try again",
          });
        },
      },
    );
  };

  const handleDeleteNote = (noteId: string) => {
    if (!customerId) return;

    deleteCustomerNote(
      { customerId, noteId },
      {
        onSuccess: () => {
          toast.success("Note deleted successfully");
        },
        onError: (error: Error) => {
          toast.error("Failed to delete note", {
            description:
              error instanceof Error ? error.message : "Please try again",
          });
        },
      },
    );
  };

  return (
    <Stack className="h-full" gap="none" fullWidth>
      <div className="mb-4 mt-4 w-full">
        <Stack gap="md" fullWidth>
          <ExpandableTextarea
            placeholder="New note"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            initialHeight="h-14"
            expandedHeight="h-36"
            className="resize-none"
            disabled={isPending || !customerId}
            onSubmit={noteText.trim() ? handleAddNote : undefined}
          />
        </Stack>
      </div>

      {notes.length === 0 ? (
        <Stack className="items-center justify-center px-6 py-6" fullWidth>
          <Typography variant="bodyW2" textColor="tertiary">
            No notes for this customer
          </Typography>
        </Stack>
      ) : (
        <div className="min-h-0 w-full flex-1">
          <ScrollArea className="h-full pr-2">
            <div className="pb-6">
              <Stack gap="none" fullWidth>
                {sortedNotes.map((note, index) => (
                  <NoteItem
                    key={note.id}
                    note={note}
                    isLast={index === sortedNotes.length - 1}
                    customerId={customerId}
                    onDeleteNote={handleDeleteNote}
                  />
                ))}
              </Stack>
            </div>
          </ScrollArea>
        </div>
      )}
    </Stack>
  );
};
